import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

// ----------------- utils -----------------
function cn(...c) { return c.filter(Boolean).join(" "); }
function pad2(n) { return String(n).padStart(2, "0"); }

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function monthOptionLabel(m1to12) {
  const m = Number(m1to12);
  const name = MONTHS[m - 1] || `Month ${m}`;
  return `${name} - ${pad2(m)}`;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}`;
}

function startOfMonth(year, month1to12) {
  return new Date(year, month1to12 - 1, 1, 0, 0, 0, 0);
}
function endOfMonthExclusive(year, month1to12) {
  return new Date(year, month1to12, 1, 0, 0, 0, 0); // first day next month
}

function statusBadgeClass(status) {
  switch (status) {
    case "done":
    case "completed":
      return "bg-green-50 text-green-700 border-green-200";
    case "in_progress":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "cancelled":
      return "bg-gray-50 text-gray-700 border-gray-200";
    default:
      return "bg-yellow-50 text-yellow-800 border-yellow-200"; // open
  }
}

function Select({ className, ...props }) {
  return (
    <select
      {...props}
      className={cn(
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2",
        "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300",
        className
      )}
    />
  );
}

function GhostButton({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-white hover:bg-gray-50 disabled:bg-gray-50 text-gray-900 font-semibold",
        "px-4 py-2 rounded-lg border border-gray-200 transition",
        className
      )}
    />
  );
}

function Pill({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1 rounded-full text-sm font-semibold border transition",
        active
          ? "bg-purple-600 text-white border-purple-600"
          : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
      )}
    >
      {children}
    </button>
  );
}

// ----------------- main -----------------
export default function PPMScheduleViewer() {
  const now = new Date();

  const [view, setView] = useState("day"); // "day" | "machine"
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  // day view selection
  const [selectedDateIso, setSelectedDateIso] = useState(isoDate(now));

  // machine view selection
  const [selectedAssetId, setSelectedAssetId] = useState("");

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [jobs, setJobs] = useState([]);

  // fetch jobs for month (single query powers both views)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        const start = Timestamp.fromDate(startOfMonth(year, month));
        const end = Timestamp.fromDate(endOfMonthExclusive(year, month));

        // NOTE: requires an index on dueDate if you also add other where clauses later
        const qy = query(
          collection(db, "ppmjobs"),
          where("dueDate", ">=", start),
          where("dueDate", "<", end)
        );

        const snap = await getDocs(qy);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // sort by dueDate ascending
        rows.sort((a, b) => {
          const da = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(0);
          const dbb = b.dueDate?.toDate ? b.dueDate.toDate() : new Date(0);
          return da - dbb;
        });

        if (cancelled) return;

        setJobs(rows);

        // default machine in machine view
        if (!selectedAssetId) {
          const firstAsset = rows.find((r) => r.assetId)?.assetId || "";
          setSelectedAssetId(firstAsset);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load ppmjobs.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  // derived list of machines from jobs
  const machines = useMemo(() => {
    const map = new Map();
    for (const j of jobs) {
      if (!j.assetId) continue;
      if (!map.has(j.assetId)) {
        map.set(j.assetId, {
          assetId: j.assetId,
          assetName: j.assetName || "Unnamed machine",
          assetCode: j.assetCode || "",
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      const an = `${a.assetName} ${a.assetCode}`.toLowerCase();
      const bn = `${b.assetName} ${b.assetCode}`.toLowerCase();
      return an.localeCompare(bn);
    });
  }, [jobs]);

  // Day view: jobs for selected date
  const dayJobs = useMemo(() => {
    return jobs.filter((j) => {
      const d = j.dueDate?.toDate ? j.dueDate.toDate() : null;
      if (!d) return false;
      return isoDate(d) === selectedDateIso;
    });
  }, [jobs, selectedDateIso]);

  // Machine view: jobs for machine in this month, grouped by date
  const machineJobs = useMemo(() => {
    if (!selectedAssetId) return [];
    return jobs.filter((j) => j.assetId === selectedAssetId);
  }, [jobs, selectedAssetId]);

  const machineJobsGrouped = useMemo(() => {
    const groups = new Map(); // iso -> []
    for (const j of machineJobs) {
      const d = j.dueDate?.toDate ? j.dueDate.toDate() : null;
      if (!d) continue;
      const key = isoDate(d);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(j);
    }
    return Array.from(groups.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([dateIso, items]) => ({ dateIso, items }));
  }, [machineJobs]);

  // quick month calendar grid (simple, not your weekly picker)
  const calendarDays = useMemo(() => {
    const dim = new Date(year, month, 0).getDate();
    const first = new Date(year, month - 1, 1);
    const firstDowMon = (first.getDay() + 6) % 7; // Mon=0..Sun=6

    const cells = [];
    for (let i = 0; i < firstDowMon; i++) cells.push(null);
    for (let day = 1; day <= dim; day++) {
      cells.push(new Date(year, month - 1, day, 0, 0, 0, 0));
    }
    return cells;
  }, [year, month]);

  const ymLabel = `${monthOptionLabel(month)} ${year}`;

  if (loading) return <div className="p-6 text-gray-700">Loading schedule…</div>;

  return (
    <div className="p-6 max-w-6xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">PPM Schedule</h1>
          <p className="text-gray-600 mt-1">
            {view === "day"
              ? "Pick a date to see all jobs due that day."
              : "Pick a machine to see all jobs due in the selected month."}
          </p>
        </div>

        <div className="flex gap-2">
          <Pill active={view === "day"} onClick={() => setView("day")}>Day</Pill>
          <Pill active={view === "machine"} onClick={() => setView("machine")}>Machine</Pill>
        </div>
      </div>

      {err ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
          <div className="font-bold">Problem</div>
          <div className="mt-1">{err}</div>
        </div>
      ) : null}

      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-xs font-semibold text-gray-500">Year</div>
          <Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-xs font-semibold text-gray-500">Month</div>
          <Select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{monthOptionLabel(m)}</option>
            ))}
          </Select>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3 md:col-span-2">
          <div className="text-xs font-semibold text-gray-500">Selected period</div>
          <div className="text-lg font-extrabold text-gray-900 mt-1">{ymLabel}</div>
          <div className="text-sm text-gray-600">
            Jobs loaded: <span className="font-semibold">{jobs.length}</span>
          </div>
        </div>
      </div>

      {/* Layout */}
      {view === "day" ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Calendar */}
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="text-lg font-extrabold text-gray-900">Calendar</div>
              <div className="text-sm text-gray-600">{ymLabel}</div>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-7 gap-2 text-xs font-semibold text-gray-500 mb-2">
                <div>Mon</div><div>Tue</div><div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div><div>Sun</div>
              </div>

              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((d, i) => {
                  if (!d) return <div key={`e_${i}`} />;

                  const key = isoDate(d);
                  const count = jobs.filter((j) => {
                    const jd = j.dueDate?.toDate ? j.dueDate.toDate() : null;
                    return jd && isoDate(jd) === key;
                  }).length;

                  const selected = key === selectedDateIso;

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedDateIso(key)}
                      className={cn(
                        "h-12 rounded-lg border text-sm font-semibold transition relative",
                        "border-gray-200 hover:bg-gray-50",
                        selected && "bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-100"
                      )}
                      title={key}
                    >
                      {d.getDate()}
                      {count ? (
                        <span
                        className="absolute bottom-1 right-1 text-[9px] leading-none font-bold text-purple-700"
                        title={`${count} job(s)`}
                        >
                        {count}
                        </span>

                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Day list */}
          <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-extrabold text-gray-900">Jobs for {selectedDateIso}</div>
                <div className="text-sm text-gray-600">
                  {dayJobs.length ? `${dayJobs.length} job(s)` : "No jobs due"}
                </div>
              </div>
            </div>

            {!dayJobs.length ? (
              <div className="p-6 text-gray-600">Nothing due on this day.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {dayJobs.map((j) => (
                  <div key={j.id} className="p-4 flex items-start justify-between gap-3">
                    <div>
                      <div className="font-extrabold text-gray-900">{j.taskName || "Untitled task"}</div>
                      <div className="text-sm text-gray-700 mt-1">
                        {j.assetName || "Machine"} {j.assetCode ? `(${j.assetCode})` : ""}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Engineer: <span className="font-semibold">{j.assignedEngineerName || "—"}</span>
                      </div>
                    </div>
                    <span className={cn("text-xs font-semibold px-2 py-1 rounded-full border", statusBadgeClass(j.status))}>
                      {j.status || "open"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Machine picker */}
          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="text-lg font-extrabold text-gray-900">Machines</div>
              <div className="text-sm text-gray-600">From scheduled jobs in this month</div>
            </div>

            <div className="p-4 space-y-2">
              <Select value={selectedAssetId} onChange={(e) => setSelectedAssetId(e.target.value)}>
                <option value="">Select machine…</option>
                {machines.map((m) => (
                  <option key={m.assetId} value={m.assetId}>
                    {m.assetName}{m.assetCode ? ` (${m.assetCode})` : ""}
                  </option>
                ))}
              </Select>

              <div className="text-xs text-gray-500">
                If a machine isn’t here, it simply has no scheduled jobs in this month yet.
              </div>
            </div>
          </div>

          {/* Machine month list */}
          <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-extrabold text-gray-900">
                  {selectedAssetId
                    ? `Jobs for machine (${ymLabel})`
                    : "Pick a machine"}
                </div>
                <div className="text-sm text-gray-600">
                  {selectedAssetId ? `${machineJobs.length} job(s)` : "—"}
                </div>
              </div>
              {selectedAssetId ? (
                <GhostButton type="button" onClick={() => setSelectedAssetId("")}>
                  Clear
                </GhostButton>
              ) : null}
            </div>

            {!selectedAssetId ? (
              <div className="p-6 text-gray-600">Select a machine to see its month list.</div>
            ) : machineJobs.length === 0 ? (
              <div className="p-6 text-gray-600">No scheduled jobs for this machine in this month.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {machineJobsGrouped.map((g) => (
                  <div key={g.dateIso} className="p-4">
                    <div className="font-extrabold text-gray-900">{g.dateIso}</div>
                    <div className="mt-2 space-y-2">
                      {g.items.map((j) => (
                        <div
                          key={j.id}
                          className="rounded-xl border border-gray-200 p-3 flex items-start justify-between gap-3"
                        >
                          <div>
                            <div className="font-extrabold text-gray-900">{j.taskName || "Untitled task"}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              Engineer: <span className="font-semibold">{j.assignedEngineerName || "—"}</span>
                            </div>
                          </div>
                          <span className={cn("text-xs font-semibold px-2 py-1 rounded-full border", statusBadgeClass(j.status))}>
                            {j.status || "open"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
